import { isFuture } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COLLECTION_TYPE } from '@app/config/constants';
import { getCollectionTypeById } from '@app/store/collections/selectors';
import { updateItem } from '@app/store/items/actions';
import { getDetailByItemId, getItemByItemId } from '@app/store/items/selectors';
import { getDetailUrlExpiryDate } from '@app/transformers/item';
import type { Item } from '@app/types/items';
import { isFileItem } from '@app/types/items';
import API, { isApiError } from '@module/api';
import Monitor from '@module/Monitor';

const preloadImage = async (src: string) =>
  new Promise<boolean>((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(true);
    };
    img.onerror = () => {
      resolve(false);
    };
    img.src = src;
  });

const wait = async (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function useItemDetailUrl(id?: Item['id']) {
  const dispatch = useDispatch();

  const detail = useSelector(getDetailByItemId({ id }));
  const item = useSelector(getItemByItemId({ id }));
  const collectionType = useSelector(
    getCollectionTypeById({ id: item?.collectionId })
  );
  // Open (publicly shared) collections resolve previews through the
  // unauthenticated public endpoint (GET /v2/web/previews — the `/web` routes
  // are the public, unauthenticated ones); personal (private/unsorted)
  // collections go through the authenticated GET /v2/previews. Mirrors the
  // download flow's derivation (see store/items/actions.ts) so it keys off
  // collection type, not the viewer's auth status — a logged-in visitor to a
  // shared board still uses the public route.
  const isPublic =
    collectionType === COLLECTION_TYPE.COLLABORATIVE ||
    collectionType === COLLECTION_TYPE.SHARED ||
    collectionType === COLLECTION_TYPE.PUBLIC;
  const [state, setState] = useState<{
    id?: Item['id'];
    isLoading: boolean;
    url?: string;
  }>({
    id,
    isLoading: true,
  });

  const fetchDetailUrl = useCallback(async () => {
    if (item === undefined) {
      return;
    }

    try {
      // For image and video items we use the raw `download_url` (already on
      // the item) directly — the browser plays mp4/webm natively and an
      // `<img>` renders the full-resolution file. Using the preview service
      // would return a downscaled IMAGE URL even for videos, breaking
      // playback. Guard on `downloadUrlExpiresAt` so we don't cache a stale
      // URL that would re-trigger this effect in a loop.
      const canPlayDownloadUrl =
        isFileItem(item) &&
        (item.fileType === 'image' || item.fileType === 'video') &&
        Boolean(item.url) &&
        (!(item.downloadUrlExpiresAt instanceof Date) ||
          isFuture(item.downloadUrlExpiresAt));
      const url = canPlayDownloadUrl
        ? item.url
        : await API.items.fetchDetailUrl({
            collectionId: item.collectionId,
            itemId: item.id,
            size: 'large',
            isPublic,
          });

      if (url === null) {
        setState({ isLoading: false, id: item.id });
        return;
      }

      if (detail.type === 'image') {
        const success = await preloadImage(url);

        // We retry once on a failure
        if (!success) {
          await wait(500);
          await preloadImage(url);
        }
      }

      // When we reused the item's download_url, cache with its own expiry if
      // known, else fall back to parsing the URL, else null (= never expires
      // per `getDetailByItemId`). Returning `undefined` here would cause the
      // selector to treat the cache as invalid and re-trigger this effect in
      // a loop, since many signed URLs (e.g. S3 v4) do not expose a
      // parseable `Expires` query param.
      const expiresAt = canPlayDownloadUrl
        ? item.downloadUrlExpiresAt instanceof Date
          ? item.downloadUrlExpiresAt
          : getDetailUrlExpiryDate(url)
        : getDetailUrlExpiryDate(url);

      dispatch(
        updateItem({
          itemId: item.id,
          item: {
            detailUrl: url,
            currentDetailUrlExpiresAt: expiresAt,
          },
        })
      );
    } catch (error) {
      setState({ isLoading: false, id: item.id });
      if (isApiError(error) && error.status === 404) {
        return;
      }
      Monitor.error(error);
    }
  }, [dispatch, item, detail, isPublic]);

  useEffect(() => {
    setState({ isLoading: id !== undefined, id });
  }, [id]);

  useEffect(() => {
    if (detail.url !== undefined) {
      setState((state) => ({
        isLoading: false,
        url: detail.url,
        id: state.id,
      }));
      return;
    }

    if (detail.type === 'web' || detail.type === 'unsupported') {
      setState((state) => ({
        isLoading: false,
        id: state.id,
      }));
      return;
    }

    void fetchDetailUrl();
  }, [detail, fetchDetailUrl]);

  if (id !== state.id) {
    return {
      isLoading: id !== undefined,
    };
  }

  return {
    isLoading: state.isLoading,
    url: state.url,
  };
}
